import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import CheckColoredIcon from '@/assets/icon/ic_check_colored.svg';
import FlagIcon from '@/assets/icon/ic_flag_colored.svg';
import PencilIcon from '@/assets/icon/ic_pencil_colored.svg';
import SirenIcon from '@/assets/icon/ic_siren_colored.svg';
import SirenOutlineIcon from '@/assets/icon/ic_siren_outline.svg';
import ThumbsUpBlueIcon from '@/assets/icon/ic_thumbsup_blue.svg';
import ThumbsUpOutlineIcon from '@/assets/icon/ic_thumbsup_outline.svg';
import ThumbsUpYellowIcon from '@/assets/icon/ic_thumbsup_yellow.svg';
import { SccPressable } from '@/components/SccPressable';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { useExperimentVariant } from '@/hooks/useExperiment';
export default function V2BottomBar({ accessibility, isUpvoted, totalUpvoteCount: _totalUpvoteCount, onPressUpvote, onPressRegister, onPressWriteReview, onPressSiren, }) {
    const insets = useSafeAreaInsets();
    const upvoteVariant = useExperimentVariant('UPVOTE_BUTTON_STYLE');
    const isIconOnly = upvoteVariant === 'TREATMENT_1';
    const hasAccessibility = !!(accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibility) ||
        !!(accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibility);
    if (isIconOnly) {
        return (_jsx(Container, { bottomInset: insets.bottom, children: _jsxs(ButtonRow, { children: [_jsxs(T1ActionButton, { elementName: "v2_place_detail_bottom_bar_register_button", logParams: { experimentVariant: upvoteVariant }, onPress: onPressRegister, children: [_jsx(FlagIcon, { width: 20, height: 20 }), _jsx(T1ActionButtonText, { numberOfLines: 1, children: "\uC815\uBCF4\uB4F1\uB85D" })] }), _jsxs(T1ActionButton, { elementName: "v2_place_detail_bottom_bar_review_button", logParams: { experimentVariant: upvoteVariant }, onPress: onPressWriteReview, children: [_jsx(PencilIcon, { width: 20, height: 20 }), _jsx(T1ActionButtonText, { numberOfLines: 1, children: "\uB9AC\uBDF0\uC791\uC131" })] }), hasAccessibility && (_jsx(T1IconUpvoteButton, { elementName: "v2_place_detail_bottom_bar_upvote_icon_button", logParams: { experimentVariant: upvoteVariant }, onPress: onPressUpvote, children: isUpvoted ? (_jsx(ThumbsUpBlueIcon, { width: 24, height: 24 })) : (_jsx(ThumbsUpOutlineIcon, { width: 24, height: 24 })) })), _jsx(T1SirenButton, { elementName: "v2_place_detail_bottom_bar_siren_button", logParams: { experimentVariant: upvoteVariant }, onPress: onPressSiren, children: _jsx(SirenOutlineIcon, { width: 24, height: 24 }) })] }) }));
    }
    return (_jsx(Container, { bottomInset: insets.bottom, children: _jsxs(ButtonRow, { children: [hasAccessibility && (_jsxs(UpvoteButton, { isUpvoted: isUpvoted, elementName: "v2_place_detail_bottom_bar_upvote_button", logParams: { experimentVariant: upvoteVariant }, onPress: onPressUpvote, children: [isUpvoted ? (_jsx(CheckColoredIcon, { width: 16, height: 16 })) : (_jsx(ThumbsUpYellowIcon, { width: 16, height: 16 })), _jsx(UpvoteText, { isUpvoted: isUpvoted, numberOfLines: 1, children: isUpvoted ? '도움됐어요' : '도움돼요' })] })), _jsxs(RegisterActionButton, { isPrimary: !hasAccessibility, elementName: "v2_place_detail_bottom_bar_register_button", logParams: { experimentVariant: upvoteVariant }, onPress: onPressRegister, children: [_jsx(FlagIcon, { width: 16, height: 16, color: !hasAccessibility ? color.white : undefined }), _jsx(RegisterActionButtonText, { isPrimary: !hasAccessibility, numberOfLines: 1, children: "\uC815\uBCF4\uB4F1\uB85D" })] }), _jsxs(ActionButton, { elementName: "v2_place_detail_bottom_bar_review_button", logParams: { experimentVariant: upvoteVariant }, onPress: onPressWriteReview, children: [_jsx(PencilIcon, { width: 16, height: 16 }), _jsx(ActionButtonText, { numberOfLines: 1, children: "\uB9AC\uBDF0\uC791\uC131" })] }), _jsx(SirenButton, { elementName: "v2_place_detail_bottom_bar_siren_button", logParams: { experimentVariant: upvoteVariant }, onPress: onPressSiren, children: _jsx(SirenIcon, { width: 20, height: 20 }) })] }) }));
}
// --- Shared ---
const Container = styled.View `
  background-color: ${color.white};
  shadow-color: #000;
  shadow-offset: 0px -4px;
  shadow-opacity: 0.12;
  shadow-radius: 16px;
  padding-top: 12px;
  padding-bottom: ${({ bottomInset }) => 24 + bottomInset}px;
  padding-horizontal: 20px;
`;
const ButtonRow = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;
// --- CONTROL styled components ---
const UpvoteButton = styled(SccPressable) `
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 0px;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${({ isUpvoted }) => (isUpvoted ? '#D6EBFF' : color.brand40)};
`;
const UpvoteText = styled.Text.attrs({
    style: { includeFontPadding: false, textAlignVertical: 'center' },
}) `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: ${({ isUpvoted }) => (isUpvoted ? '#000000' : color.white)};
  flex-shrink: 1;
`;
const ActionButton = styled(SccPressable) `
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 0px;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${color.gray15};
`;
const ActionButtonText = styled.Text.attrs({
    style: { includeFontPadding: false, textAlignVertical: 'center' },
}) `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: #24262b;
  flex-shrink: 1;
`;
const RegisterActionButton = styled(SccPressable) `
  flex: 1;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 0px;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${({ isPrimary }) => isPrimary ? color.brand40 : color.gray15};
`;
const RegisterActionButtonText = styled.Text.attrs({
    style: { includeFontPadding: false, textAlignVertical: 'center' },
}) `
  font-family: ${font.pretendardMedium};
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  color: ${({ isPrimary }) => (isPrimary ? color.white : '#24262b')};
  flex-shrink: 1;
`;
const SirenButton = styled(SccPressable) `
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: ${color.gray15};
`;
// --- TREATMENT_1 styled components ---
const T1ActionButton = styled(SccPressable) `
  flex: 1;
  height: 44px;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #d6ebff;
`;
const T1ActionButtonText = styled.Text.attrs({
    style: { includeFontPadding: false, textAlignVertical: 'center' },
}) `
  font-family: ${font.pretendardMedium};
  font-size: 15px;
  line-height: 22px;
  letter-spacing: -0.3px;
  color: #24262b;
  flex-shrink: 1;
`;
const T1IconUpvoteButton = styled(SccPressable) `
  width: 44px;
  height: 44px;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-width: 1px;
  border-color: #e3e4e8;
`;
const T1SirenButton = styled(SccPressable) `
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  border-width: 1px;
  border-color: #e3e4e8;
`;
